/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import qs from 'qs'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  SwimmingBooking,
  SwimmingMemberSchedule,
  SwimmingSchedule,
  SwimmingTrainerWithAvailableTime,
} from '@/types/swimming'
import { SwimmingScheduleFilter } from '@/types/swimming'

export const SwimmingService = (client: AxiosInstance) => {
  return {
    async getSchedules(
      opt: PaginationOpt & SwimmingScheduleFilter
    ): Promise<PaginationResponse<SwimmingSchedule>> {
      const { data } = await wrapApiCall(() => client.get('/swimming/schedules', { params: opt }))
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, SwimmingSchedule)
        ),
      }
    },

    async getMySchedules(
      opt: PaginationOpt & SwimmingScheduleFilter
    ): Promise<PaginationResponse<SwimmingSchedule>> {
      const { data } = await wrapApiCall(() =>
        client.get('/swimming/my-schedules', { params: opt })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, SwimmingSchedule)
        ),
      }
    },

    async getSchedule(id: string | number): Promise<SwimmingSchedule> {
      const { data } = await wrapApiCall(() => client.get(`/swimming/schedules/${id}`))
      return deserialize(data, SwimmingSchedule)
    },

    async createSchedule(data: SwimmingSchedule): Promise<SwimmingSchedule> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post('/swimming/schedules', data.serializeToCreate())
      )
      return deserialize(responseData, SwimmingSchedule)
    },

    async createScheduleWithBooking(data: SwimmingSchedule): Promise<SwimmingSchedule> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post('/swimming/schedules/with-booking', data.serializeToCreateWithBooking())
      )
      return deserialize(responseData, SwimmingSchedule)
    },

    async updateSchedule(id: string | number, data: SwimmingSchedule): Promise<SwimmingSchedule> {
      const { data: responseData } = await wrapApiCall(() =>
        client.patch(`/swimming/schedules/${id}`, data.serializeToCreate())
      )
      return deserialize(responseData, SwimmingSchedule)
    },

    async cancelSchedule(id: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() => client.patch(`/swimming/schedules/${id}/cancel`))
      return !!data?.success
    },

    async deleteSchedule(id: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() => client.delete(`/swimming/schedules/${id}`))
      return !!data?.success
    },

    async deleteScheduleGroup(groupId: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete(`/swimming/schedules-group/${groupId}`)
      )
      return !!data?.success
    },

    async getAvailableTrainers(
      id: string | number,
      filter: {
        startTime: string
        endTime: string
        roleIds: number[]
      }
    ): Promise<SwimmingTrainerWithAvailableTime[]> {
      const { data } = await wrapApiCall(() =>
        client.get(`/swimming/schedules/${id}/available-trainers`, {
          params: filter,
          paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
        })
      )
      return data?.map((item: Record<string, unknown>) =>
        deserialize(item, SwimmingTrainerWithAvailableTime)
      )
    },

    async updateTrainers(id: string | number, trainerIds: number[]): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/swimming/schedules/${id}/trainers`, { trainerIds })
      )
      return !!data.success
    },

    async editSession(
      id: string | number,
      payload: {
        scope: 'single' | 'future'
        // single
        startTime?: string
        endTime?: string
        trainerIds?: number[]
        // future
        endDate?: string
        singleTimeDurations?: {
          dayOfWeekIndex: number
          startTime: string
          endTime: string
          trainerId?: number
        }[]
        cancelBefore?: number
      }
    ): Promise<SwimmingSchedule[]> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/swimming/schedules/${id}/edit-session`, payload)
      )
      return Array.isArray(data)
        ? data.map((item: Record<string, unknown>) => deserialize(item, SwimmingSchedule))
        : []
    },

    async getScheduleMembers(id: string | number): Promise<SwimmingMemberSchedule[]> {
      const { data } = await wrapApiCall(() => client.get(`/swimming/schedules/${id}/members`))
      return data?.map((item: Record<string, unknown>) => deserialize(item, SwimmingMemberSchedule))
    },

    async getScheduleBookings(id: string | number): Promise<SwimmingBooking[]> {
      const { data } = await wrapApiCall(() => client.get(`/swimming/schedules/${id}/bookings`))
      return data?.map((item: Record<string, unknown>) => deserialize(item, SwimmingBooking))
    },
  }
}
